import { Grid, Tab, Table, TableBody, Typography } from "@mui/material";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { AppMessageBox } from "components/AppMessageBox";
import { AppTabs } from "components/AppTabs";
import { SpinnerIndicator } from "components/CallServer";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPagination from "components/MuiExtras/MuiPagination";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils from "lib/LibUtils";
import { DashboardAwardAnalysisDetailColumn, DashboardAwardAnalysisPeriod, DashboardBadgeModel, PagedResult } from "models/ApiModels";
import moment from "moment";
import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { DashboardAwardAnalysisProps } from "views/Dashboards/AwardAnalysisDashboard/Common";
import { RenderBadgeModelList } from "views/Dashboards/components/RenderBadgeModelList";
import UserListDrawer from "views/Dashboards/components/UserListDrawer";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    dialog: any;
    data: PagedResult<DashboardBadgeModel>;
    offset: number;
    loading: boolean;
    tabOption: DashboardAwardAnalysisPeriod;
}

interface IProps extends DashboardAwardAnalysisProps {
}

type Props = IProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class AwardAnalysisBadgeListComponent extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            offset: null,
            loading: true,
            tabOption: DashboardAwardAnalysisPeriod.Last30
        };
    }

    ///
    private getDateFilter() {

        if (this.state.tabOption == DashboardAwardAnalysisPeriod.Total)
            return { startDate: null, endDate: null };

        var de = moment().endOf("day");
        var ds = moment().endOf("day");
        ds.add("days", -29);
        return { startDate: ds.toDate(), endDate: de.toDate() };
    }

    ///
    private loadData(offset: number, scrollToStartOfList: boolean = false) {

        this.setState({ loading: true });

        api.getDashboardAwardAnalysis(
            {
                detailColumn: DashboardAwardAnalysisDetailColumn.Badges,
                ...this.getDateFilter(),
                ...this.props.filter,
                drillDownFilter: null,
                range: { offset, take: DefaultListParameters.viewRecordsByPage },
            })
            .then((result) => {
                this.setState({
                    data: result,
                    loading: false,
                    offset: offset
                }, () => {
                    if (scrollToStartOfList) HtmlUtils.scrollToElement("divList");
                });

            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }

    ///
    private drillDown = (item: DashboardBadgeModel) => {

        this.setState({
            dialog: (
                <UserListDrawer
                    title={this.props.t("Views.Dashboards.AwardAnalysis.BadgeList.UserListTitle")}
                    subtitle={item.name}
                    showTotalColumn={true}
                    onGetData={
                        (searchtext, offset) =>
                            api.getDashboardAwardAnalysis({
                                detailColumn: DashboardAwardAnalysisDetailColumn.BadgesDrillDown,
                                ...this.getDateFilter(),
                                ...this.props.filter,
                                drillDownFilter: {
                                    id: item.badgeId,
                                    title: searchtext,
                                },
                                range: { offset: offset, take: 20 },
                            })
                    }
                    onClose={
                        () => this.setState({ dialog: null })
                    }
                />
            )
        });
    };

    ///
    private renderHeader() {
        const { tabOption } = this.state;

        return (
            <div id="divList" >
                <MuiContainer margin="normal">
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <Typography component="p" variant="h6"><b>{this.props.t("Views.Dashboards.AwardAnalysis.BadgeList.HeaderTitle").toString().toLocaleUpperCase()}</b></Typography>
                        </Grid>

                        {
                            <Grid item>
                                <AppTabs
                                    value={tabOption}
                                    onChange={
                                        (e, newValue: DashboardAwardAnalysisPeriod) => {
                                            this.setState({ tabOption: newValue }, () => this.loadData(0));
                                        }
                                    }
                                    indicatorColor="primary"
                                    textColor="primary"
                                >
                                    <Tab label={this.props.t<string>("Views.Dashboards.Common.LastMonth")} value={DashboardAwardAnalysisPeriod.Last30} />
                                    <Tab label={this.props.t<string>("Views.Dashboards.Common.FullPeriod")} value={DashboardAwardAnalysisPeriod.Total} />
                                </AppTabs>

                            </Grid>
                        }
                    </Grid>
                </MuiContainer>
            </div>
        );
    }

    ///
    private renderList() {
        const { data, dialog, loading, offset } = this.state;

        const dataEmpty = this.props.t("Views.Dashboards.AwardAnalysis.BadgeList.EmptyData");

        return (
            <MuiContainer margin="normal">
                <div>
                    {dialog}
                    <br />
                    {this.renderHeader()}

                    {
                        loading
                            ? <SpinnerIndicator />
                            : data.total == 0
                                ? <MuiMessageBox variant="warning" message={dataEmpty} />
                                : (
                                    <>
                                        <MuiContainer>
                                            <Table size="small">
                                                <TableBody >
                                                    <RenderBadgeModelList
                                                        indexOffset={offset}
                                                        items={data.items}
                                                        onDrillDown={this.drillDown}
                                                    />
                                                </TableBody>
                                            </Table>
                                        </MuiContainer>
                                        <MuiPagination
                                            limit={DefaultListParameters.viewRecordsByPage}
                                            offset={offset}
                                            total={data.total}
                                            onClick={(e, offset) => this.loadData(offset, true)}
                                        />
                                    </>
                                )
                    }
                </div>
            </MuiContainer >
        );
    }

    ///
    public componentDidUpdate(prevProps: IProps) {
        if (!LibUtils.isEqual(this.props.filter, prevProps.filter)) {
            this.loadData(0);
        }
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    ///
    public render() {

        return (
            <div>
                {
                    !this.state.loading && this.state.data == null
                        ? <AppMessageBox resource="LoadListDataFailed" />
                        : this.renderList()
                }
            </div>
        );
    }
}

export const AwardAnalysisBadgeList = withTranslation()(AwardAnalysisBadgeListComponent);